/**
 * Simplified Morph Implementation (inspired by idiomorph)
 * Morphs an existing DOM element to match new content while preserving state
 *
 * @param {HTMLElement} oldNode - The existing DOM element
 * @param {HTMLElement} newNode - The new content to morph into
 * @param {Object} options - Optional configuration
 */
export function morph(oldNode, newNode, options = {}) {
  if (!oldNode || !newNode) return;

  const {
    callbacks = {},
    ignoreActiveValue = false,
  } = options;

  // Call beforeNodeMorphed callback
  if (callbacks.beforeNodeMorphed) {
    callbacks.beforeNodeMorphed(oldNode, newNode);
  }

  // Special handling for inputs to preserve state
  if (oldNode.tagName === 'INPUT' && !ignoreActiveValue) {
    if (oldNode === document.activeElement) {
      // Preserve cursor position and value for active inputs
      const selectionStart = oldNode.selectionStart;
      const selectionEnd = oldNode.selectionEnd;

      syncNodeAttributes(oldNode, newNode);

      if (oldNode.type !== 'file') {
        oldNode.value = newNode.value;
      }

      if (selectionStart !== null) {
        oldNode.setSelectionRange(selectionStart, selectionEnd);
      }

      return;
    }
  }

  // Special handling for textareas
  if (oldNode.tagName === 'TEXTAREA' && !ignoreActiveValue) {
    if (oldNode === document.activeElement) {
      const selectionStart = oldNode.selectionStart;
      const selectionEnd = oldNode.selectionEnd;

      syncNodeAttributes(oldNode, newNode);
      oldNode.value = newNode.value;

      if (selectionStart !== null) {
        oldNode.setSelectionRange(selectionStart, selectionEnd);
      }

      return;
    }
  }

  // Special handling for select elements
  if (oldNode.tagName === 'SELECT') {
    const oldValue = oldNode.value;
    syncNodeAttributes(oldNode, newNode);
    syncSelectOptions(oldNode, newNode);

    // Try to preserve selection
    if (oldValue && oldNode.querySelector(`option[value="${oldValue}"]`)) {
      oldNode.value = oldValue;
    } else {
      oldNode.value = newNode.value;
    }

    return;
  }

  // Sync attributes
  syncNodeAttributes(oldNode, newNode);

  // Morph children
  morphChildren(oldNode, newNode, callbacks);

  // Call afterNodeMorphed callback
  if (callbacks.afterNodeMorphed) {
    callbacks.afterNodeMorphed(oldNode, newNode);
  }
}

/**
 * Syncs attributes from new node to old node
 */
function syncNodeAttributes(oldNode, newNode) {
  // Remove old attributes that don't exist in new node
  for (const attr of oldNode.attributes) {
    if (!newNode.hasAttribute(attr.name)) {
      oldNode.removeAttribute(attr.name);
    }
  }

  // Add/update attributes from new node
  for (const attr of newNode.attributes) {
    if (oldNode.getAttribute(attr.name) !== attr.value) {
      oldNode.setAttribute(attr.name, attr.value);
    }
  }
}

/**
 * Syncs select element options
 */
function syncSelectOptions(oldSelect, newSelect) {
  const oldOptions = Array.from(oldSelect.options);
  const newOptions = Array.from(newSelect.options);

  // Remove options that don't exist in new select
  oldOptions.forEach((oldOption, index) => {
    const newOption = newOptions[index];
    if (!newOption || oldOption.value !== newOption.value || oldOption.text !== newOption.text) {
      oldOption.remove();
    }
  });

  // Add new options
  newOptions.forEach((newOption, index) => {
    const oldOption = oldSelect.options[index];
    if (!oldOption) {
      oldSelect.add(newOption.cloneNode(true));
    } else if (oldOption.value !== newOption.value || oldOption.text !== newOption.text) {
      oldSelect.options[index] = newOption.cloneNode(true);
    }
  });
}

/**
 * Morphs children of the old node to match new node
 */
function morphChildren(oldParent, newParent, callbacks) {
  const oldChildren = Array.from(oldParent.childNodes);
  const newChildren = Array.from(newParent.childNodes);

  let oldIndex = 0;
  let newIndex = 0;

  while (oldIndex < oldChildren.length || newIndex < newChildren.length) {
    const oldChild = oldChildren[oldIndex];
    const newChild = newChildren[newIndex];

    // No more new children - remove remaining old children
    if (!newChild) {
      if (callbacks.beforeNodeRemoved) {
        callbacks.beforeNodeRemoved(oldChild);
      }
      oldChild.remove();
      oldIndex++;
      continue;
    }

    // No more old children - append remaining new children
    if (!oldChild) {
      const clonedNode = newChild.cloneNode(true);
      if (callbacks.beforeNodeAdded) {
        callbacks.beforeNodeAdded(clonedNode);
      }
      oldParent.appendChild(clonedNode);
      reinjectScripts(clonedNode);
      if (callbacks.afterNodeAdded) {
        callbacks.afterNodeAdded(clonedNode);
      }
      newIndex++;
      continue;
    }

    // Both nodes exist - determine if they match
    if (nodesMatch(oldChild, newChild)) {
      // Nodes match - morph if element, replace if text
      if (oldChild.nodeType === Node.ELEMENT_NODE) {
        morph(oldChild, newChild, { callbacks });
      } else if (oldChild.nodeType === Node.TEXT_NODE) {
        if (oldChild.nodeValue !== newChild.nodeValue) {
          oldChild.nodeValue = newChild.nodeValue;
        }
      }
      oldIndex++;
      newIndex++;
    } else {
      // Nodes don't match - replace old with new
      const clonedNode = newChild.cloneNode(true);
      if (callbacks.beforeNodeRemoved) {
        callbacks.beforeNodeRemoved(oldChild);
      }
      if (callbacks.beforeNodeAdded) {
        callbacks.beforeNodeAdded(clonedNode);
      }
      oldParent.replaceChild(clonedNode, oldChild);
      reinjectScripts(clonedNode);
      if (callbacks.afterNodeAdded) {
        callbacks.afterNodeAdded(clonedNode);
      }
      oldIndex++;
      newIndex++;
    }
  }
}

/**
 * Checks if two nodes match (same type and key)
 */
function nodesMatch(node1, node2) {
  if (node1.nodeType !== node2.nodeType) return false;

  if (node1.nodeType === Node.ELEMENT_NODE) {
    // Check tag name
    if (node1.tagName !== node2.tagName) return false;

    // Check ID (used as key)
    const id1 = node1.getAttribute('id');
    const id2 = node2.getAttribute('id');

    if (id1 || id2) {
      return id1 === id2;
    }

    // No ID - they match if same tag
    return true;
  }

  return true;
}

/**
 * Reinjects script tags to allow execution
 */
function reinjectScripts(element) {
  if (element.tagName === 'SCRIPT') {
    const newScript = document.createElement('script');
    Array.from(element.attributes).forEach(attr => {
      newScript.setAttribute(attr.name, attr.value);
    });
    newScript.textContent = element.textContent;
    element.parentNode.replaceChild(newScript, element);
  }

  element.querySelectorAll?.('script').forEach(oldScript => {
    const newScript = document.createElement('script');
    Array.from(oldScript.attributes).forEach(attr => {
      newScript.setAttribute(attr.name, attr.value);
    });
    newScript.textContent = oldScript.textContent;
    oldScript.parentNode.replaceChild(newScript, oldScript);
  });
}
